/**
 * The read-only rows of the file-share properties panel (plan D13, Task 7.16): a share — quota, usage, protocol, tier,
 * billing model, provisioned IOPS / bandwidth, last modified, share soft delete and the snapshot count (from
 * `recoverability`); a file or folder — size, times, content type, file id, then the SMB set (attributes, times) or
 * the NFS set (mode, owner, group, type, link count), never both. Date-times render in local time; absent values are
 * left out rather than shown blank.
 */
import type { FileShareBilling, FileShareProtocolState, StorageFileItemProperties, StorageShareProperties, StorageShareRecoverability } from '../../../../../types/azureStorage';
import { formatStorageSize, formatStorageTimestamp } from '../../utils/storageItemRows';

export type PropertyRow = { key: string; label: string; value: string };

const BILLING_LABEL: Record<FileShareBilling, string> = {
  'pay-as-you-go': 'Pay-as-you-go', 'provisioned-v1': 'Provisioned v1', 'provisioned-v2': 'Provisioned v2', provisioned: 'Provisioned', unknown: 'Unknown',
};
const PROTOCOL_LABEL: Record<FileShareProtocolState, string> = { smb: 'SMB', nfs: 'NFS', unknown: 'Unknown' };
const number = (value: number) => value.toLocaleString('en-US');

function rows(entries: Array<[key: string, label: string, value: string | undefined]>): PropertyRow[] {
  return entries.filter((entry): entry is [string, string, string] => entry[2] !== undefined && entry[2] !== '').map(([key, label, value]) => ({ key, label, value }));
}
const when = (iso: string | undefined) => (iso ? formatStorageTimestamp(iso) : undefined);

export function sharePropertyRows(properties: StorageShareProperties, recoverability: StorageShareRecoverability): PropertyRow[] {
  const softDelete = recoverability.shareSoftDelete;
  const snapshots = recoverability.snapshots;
  return rows([
    ['snapshot', 'Snapshot taken', when(properties.snapshot)],
    ['quota', 'Quota', properties.quotaGiB === undefined ? undefined : `${number(properties.quotaGiB)} GiB`],
    ['usage', 'Used', properties.usageBytes === undefined ? undefined : formatStorageSize(properties.usageBytes)],
    ['protocol', 'Protocol', PROTOCOL_LABEL[properties.protocol]],
    ['tier', 'Access tier', properties.accessTier],
    ['billing', 'Billing model', properties.billing ? BILLING_LABEL[properties.billing] : undefined],
    ['iops', 'Provisioned IOPS', properties.provisionedIops === undefined ? undefined : number(properties.provisionedIops)],
    ['bandwidth', 'Provisioned bandwidth', properties.provisionedBandwidthMiBps === undefined ? undefined : `${number(properties.provisionedBandwidthMiBps)} MiB/s`],
    ['last-modified', 'Last modified', when(properties.lastModified)],
    ['next-downgrade', 'Size can be lowered after', when(properties.nextQuotaDowngradeAt)],
    ['root-squash', 'Root squash', properties.rootSquash],
    ['lease', 'Lease state', properties.leaseState],
    ['soft-delete', 'Share soft delete', softDelete.state === 'on' ? `On — ${softDelete.days} days` : softDelete.state === 'off' ? 'Off' : `Unknown — ${softDelete.reason}`],
    ['snapshots', 'Snapshots', snapshots.state === 'known' ? number(snapshots.count) : `Unknown — ${snapshots.reason}`],
    ['etag', 'ETag', properties.etag],
  ]);
}

export function itemPropertyRows(item: StorageFileItemProperties): PropertyRow[] {
  const smb = item.protocol === 'nfs' ? undefined : item.smb;
  const nfs = item.protocol === 'smb' ? undefined : item.nfs;
  return rows([
    ['kind', 'Kind', item.kind === 'file' ? 'File' : 'Folder'],
    ['path', 'Path', `${item.share}/${item.path}`],
    ['snapshot', 'Snapshot', when(item.snapshot)],
    ['protocol', 'Protocol', PROTOCOL_LABEL[item.protocol]],
    ['size', 'Size', item.kind === 'file' && item.size !== undefined ? formatStorageSize(item.size) : undefined],
    ['last-modified', 'Last modified', when(item.lastModified)],
    ['content-type', 'Content type', item.contentType],
    ['file-id', 'File ID', item.fileId],
    ['etag', 'ETag', item.etag],
    ['attributes', 'Attributes', smb?.attributes],
    ['created', 'Created', when(smb?.creationTime)],
    ['last-write', 'Last write', when(smb?.lastWriteTime)],
    ['changed', 'Changed', when(smb?.changeTime)],
    ['lease', 'Lease state', smb?.leaseState],
    ['mode', 'Mode', nfs?.mode],
    ['owner', 'Owner', nfs?.owner],
    ['group', 'Group', nfs?.group],
    ['file-type', 'File type', nfs?.fileType],
    ['link-count', 'Link count', nfs?.linkCount === undefined ? undefined : number(nfs.linkCount)],
  ]);
}
